import { Check, ChevronDown } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import styles from './Select.module.scss';

/**
 * Selector de lista con el patrón WAI-ARIA "select-only combobox" (reemplaza al `<select>` nativo):
 * - el disparador es un botón con `role="combobox"` que anuncia el valor actual y `aria-expanded`;
 * - el foco se queda en el disparador y la opción activa se señala con `aria-activedescendant`;
 * - ↓/↑ mueven la opción activa (abren la lista si estaba cerrada), Inicio/Fin van a la primera y
 *   última, Enter/Espacio eligen, Esc cierra sin elegir, Tab elige la activa y sigue;
 * - escribir letras salta a la opción cuyo texto empieza igual (typeahead).
 * Va dentro de un `<label>` sin problema: el clic en una opción no reactiva el disparador.
 */
export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SelectProps<T extends string> {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const TYPEAHEAD_RESET_MS = 500;

export function Select<T extends string>({
  value,
  options,
  onChange,
  id,
  className,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: SelectProps<T>): JSX.Element {
  const generatedId = useId();
  const triggerId = id ?? `${generatedId}-trigger`;
  const listId = `${generatedId}-list`;
  const optionId = (index: number): string => `${generatedId}-option-${index}`;

  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', timer: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) {return undefined;}
    function closeOnOutsidePress(event: globalThis.MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {setOpen(false);}
    }
    document.addEventListener('mousedown', closeOnOutsidePress);
    return (): void => document.removeEventListener('mousedown', closeOnOutsidePress);
  }, [open]);

  useEffect(() => {
    if (!open) {return;}
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  useEffect(() => {
    const state = typed.current;
    return (): void => window.clearTimeout(state.timer);
  }, []);

  function enabledFrom(start: number, step: 1 | -1): number {
    for (let i = start; i >= 0 && i < options.length; i += step) {
      if (!options[i]?.disabled) {return i;}
    }
    return -1;
  }

  function openList(index = selectedIndex): void {
    const first = enabledFrom(Math.max(index, 0), 1);
    setActive(first === -1 ? 0 : first);
    setOpen(true);
  }

  function commit(index: number): void {
    const option = options[index];
    if (option && !option.disabled) {onChange(option.value);}
    setOpen(false);
  }

  function move(index: number, step: 1 | -1): void {
    const next = enabledFrom(index, step);
    if (next !== -1) {setActive(next);}
  }

  function typeahead(char: string): void {
    window.clearTimeout(typed.current.timer);
    typed.current.text += char.toLowerCase();
    typed.current.timer = window.setTimeout(() => {
      typed.current.text = '';
    }, TYPEAHEAD_RESET_MS);
    const text = typed.current.text;
    // Con una sola letra repetida se avanza a la siguiente coincidencia; con varias se afina.
    const from = open ? active : Math.max(selectedIndex, 0);
    const ordered = [...options.keys()].map((i) => (i + from + (text.length === 1 ? 1 : 0)) % options.length);
    const hit = ordered.find((i) => {
      const option = options[i];
      return option && !option.disabled && option.label.toLowerCase().startsWith(text);
    });
    if (hit === undefined) {return;}
    if (open) {setActive(hit);} else {openList(hit);}
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.altKey || event.ctrlKey || event.metaKey) {return;}
    const { key } = event;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
        event.preventDefault();
        openList();
      } else if (key === 'Home' || key === 'End') {
        event.preventDefault();
        openList(key === 'Home' ? 0 : options.length - 1);
      } else if (key.length === 1) {
        typeahead(key);
      }
      return;
    }
    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        move(active + 1, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(active - 1, -1);
        break;
      case 'Home':
        event.preventDefault();
        move(0, 1);
        break;
      case 'End':
        event.preventDefault();
        move(options.length - 1, -1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(active);
        break;
      case 'Escape':
        // Que un modal o panel padre no se cierre con el mismo Esc.
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        commit(active);
        break;
      default:
        if (key.length === 1) {typeahead(key);}
    }
  }

  function handleOptionClick(event: MouseEvent<HTMLLIElement>, index: number): void {
    // Dentro de un <label>, el clic reactivaría el disparador y volvería a abrir la lista.
    event.preventDefault();
    commit(index);
  }

  return (
    <div ref={rootRef} className={[styles.root, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        id={triggerId}
        role="combobox"
        className={styles.trigger}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(active) : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-value={value}
        onClick={(event) => {
          // Un clic en el <label> envolvente también llega aquí: alterna igual que un clic directo.
          event.preventDefault();
          if (open) {setOpen(false);} else {openList();}
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
      >
        <span className={styles.value}>{selected?.label ?? ''}</span>
        <ChevronDown size={16} aria-hidden="true" className={styles.chevron} />
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className={styles.list}
          tabIndex={-1}
          aria-labelledby={ariaLabelledBy ?? triggerId}
          // El foco no sale del disparador al pulsar una opción.
          onMouseDown={(event) => event.preventDefault()}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              data-value={option.value}
              aria-selected={index === selectedIndex}
              aria-disabled={option.disabled || undefined}
              className={[
                styles.option,
                index === active ? styles.active : '',
                index === selectedIndex ? styles.selected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseMove={() => {
                if (!option.disabled) {setActive(index);}
              }}
              onClick={(event) => handleOptionClick(event, index)}
            >
              <span>{option.label}</span>
              {index === selectedIndex ? <Check size={14} aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
